<script>
  import { onMount } from 'svelte';
  import { auth } from '../../state/auth.svelte.js';
  import { studioSettings, toggleFavoriteModel } from '../../state/settings.svelte.js';
  import { imageModels, modelCatalog, loadImageModelCatalog, getCatalogModel } from '../../state/model-catalog.svelte.js';
  import { filterImageCatalog, getImageModelCoverage } from '../../utils/image-model-catalog.js';

  let { selectedIds = $bindable([]), network = $bindable('fast'), matureContentEnabled = false, readonly = false } = $props();
  let query = $state('');
  let family = $state('');
  let workflow = $state('');
  let coverage = $state('');
  let favoritesOnly = $state(false);
  let showUnavailable = $state(false);
  let resultLimit = $state(50);

  const families = $derived([...new Set(imageModels.map((model) => model.family))].sort());
  const visibleModels = $derived(filterImageCatalog(imageModels, {
    query, family, workflow, coverage, network, favoritesOnly, showUnavailable,
    selectedIds, favoriteIds: studioSettings.favoriteModelIds, isUnlimited: auth.isUnlimited
  }));
  const tierName = $derived(auth.subscription?.tier === 'unlimited_pro' ? 'Unlimited Pro' : 'Unlimited');
  const displayedModels = $derived(visibleModels.slice(0, resultLimit));

  $effect(() => {
    query; family; workflow; coverage; favoritesOnly; showUnavailable; network;
    resultLimit = 50;
  });

  onMount(() => { loadImageModelCatalog().catch(() => {}); });

  function toggleModel(model) {
    if (readonly) return;
    if (selectedIds.includes(model.id)) {
      if (selectedIds.length > 1) selectedIds = selectedIds.filter((id) => id !== model.id);
    } else if (model.availableNetworks.includes(network)
      && (!model.requiresMatureContent || matureContentEnabled)) {
      selectedIds = [...selectedIds, model.id];
    }
  }

  function moveModel(index, direction) {
    const next = [...selectedIds];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    selectedIds = next;
  }
</script>

<div class="image-model-picker">
  <div class="network-control" role="group" aria-label="Generation network">
    {#each ['fast', 'relaxed'] as choice}
      <button type="button" class:active={network === choice} aria-pressed={network === choice}
        disabled={readonly} onclick={() => network = choice}>{choice === 'fast' ? 'Fast' : 'Relaxed'}</button>
    {/each}
  </div>
  <p class="picker-hint">Availability is shown for the selected network. Your selected models stay in order.</p>

  {#if selectedIds.length}
    <ol class="selected-models" aria-label="Selected model order">
      {#each selectedIds as id, index (id)}
        {@const selected = getCatalogModel(id)}
        <li>
          <span>{selected?.label || id}</span>
          {#if modelCatalog.status === 'ready' && !selected?.availableNetworks.includes(network)}
            <span class="unavailable">Unavailable on {network === 'fast' ? 'Fast' : 'Relaxed'}</span>
          {/if}
          <div class="selected-actions">
            <button type="button" disabled={readonly || index === 0} aria-label="Move {selected?.label || id} earlier" onclick={() => moveModel(index, -1)}>↑</button>
            <button type="button" disabled={readonly || index === selectedIds.length - 1} aria-label="Move {selected?.label || id} later" onclick={() => moveModel(index, 1)}>↓</button>
            <button type="button" disabled={readonly || selectedIds.length === 1} aria-label="Remove {selected?.label || id}" onclick={() => selectedIds = selectedIds.filter((entry) => entry !== id)}>×</button>
          </div>
        </li>
      {/each}
    </ol>
  {/if}

  <label class="search-field">Search image models
    <input type="search" bind:value={query} placeholder="Model, family, creator…" />
  </label>
  <div class="picker-filters">
    <label>Family<select bind:value={family}><option value="">All families</option>{#each families as entry}<option value={entry}>{entry}</option>{/each}</select></label>
    <label>Workflow<select bind:value={workflow}><option value="">All workflows</option><option value="text">From text</option><option value="edit">Edit / references</option></select></label>
    <label>Billing<select bind:value={coverage}><option value="">All models</option><option value="covered">Included in my plan</option><option value="credits">Requires credits</option></select></label>
  </div>
  <div class="picker-toggles">
    <label><input type="checkbox" bind:checked={favoritesOnly} /> Favorites</label>
    <label><input type="checkbox" bind:checked={showUnavailable} /> Show unavailable</label>
    <button type="button" class="refresh-button" disabled={modelCatalog.status === 'loading'} onclick={() => loadImageModelCatalog({ force: true }).catch(() => {})}>Refresh</button>
  </div>
  {#if modelCatalog.status === 'loading'}
    <p role="status" class="picker-hint">Loading image models…</p>
  {:else if modelCatalog.error}
    <p role="alert" class="catalog-error">{modelCatalog.error}</p>
  {/if}
  <p class="picker-hint" role="status">{displayedModels.length} of {visibleModels.length} matching models shown · {selectedIds.length} selected</p>
  <div class="catalog-list">
    {#each displayedModels as model (model.id)}
      {@const selected = selectedIds.includes(model.id)}
      {@const unavailable = !model.availableNetworks.includes(network)}
      {@const matureLocked = model.requiresMatureContent && !matureContentEnabled}
      <div class="catalog-row" class:selected>
        <label class="model-choice">
          <input type="checkbox" checked={selected}
            disabled={readonly || (selected && selectedIds.length === 1) || (!selected && (unavailable || matureLocked))}
            onchange={() => toggleModel(model)} />
          <span class="model-copy">
            <span class="model-title">{model.label}</span>
            <span class="model-meta">
              {#if getImageModelCoverage(model, auth.isUnlimited) === 'unknown'}Billing unavailable
              {:else if getImageModelCoverage(model, auth.isUnlimited) === 'covered'}Included in {tierName}
              {:else if model.isPremiumOnly}Premium Spark · separately billed
              {:else if model.freeSparkEligible}Free Spark eligible
              {:else}Spark{/if}
              {#if model.requiresReference} · Reference required{/if}
              {#if matureLocked} · Enable Mature Content (18+){/if}
            </span>
            <span class="availability">
              {#if unavailable}Unavailable on this network
              {:else if model.isPartner}Partner service available
              {:else}{model.workerCounts[network] || 0} workers on {network === 'fast' ? 'Fast' : 'Relaxed'}{/if}
            </span>
            {#if model.attribution?.creator}<span class="attribution">By {model.attribution.creator}</span>{/if}
          </span>
        </label>
        <button type="button" class="favorite-button" class:favorited={studioSettings.favoriteModelIds.includes(model.id)}
          disabled={!auth.isLoggedIn} aria-pressed={studioSettings.favoriteModelIds.includes(model.id)}
          aria-label="Favorite {model.label}" onclick={() => toggleFavoriteModel(model.id)}>{studioSettings.favoriteModelIds.includes(model.id) ? '★' : '☆'}</button>
      </div>
    {/each}
    {#if modelCatalog.status === 'ready' && !visibleModels.length}<p class="picker-hint">No models match these filters.</p>{/if}
  </div>
  {#if displayedModels.length < visibleModels.length}
    <button type="button" onclick={() => resultLimit += 50}>Show {Math.min(50, visibleModels.length - displayedModels.length)} more models</button>
  {/if}
</div>

<style>
  .image-model-picker { display: grid; gap: 0.65rem; min-width: 0; }
  .network-control { display: flex; gap: 0.4rem; }
  button, input, select { font: inherit; }
  button { cursor: pointer; color: inherit; border: 1px solid var(--color-border, #464646); background: var(--color-surface, #252525); border-radius: 8px; padding: 0.45rem 0.65rem; min-height: 44px; min-width: 44px; }
  button:focus-visible, input:focus-visible, select:focus-visible { outline: 2px solid var(--color-primary, #ff1b8d); outline-offset: 2px; }
  button:disabled { opacity: 0.45; cursor: default; }
  button.active { border-color: var(--color-primary, #ff1b8d); color: var(--color-primary, #ff1b8d); }
  .picker-hint { margin: 0; font-size: 0.75rem; color: var(--color-text-muted, #666); }
  .search-field, .picker-filters label { display: grid; gap: 0.3rem; font-size: 0.75rem; }
  .search-field input, select { width: 100%; box-sizing: border-box; min-width: 0; min-height: 44px; background: var(--color-surface, #252525); color: var(--color-text, #eee); border: 1px solid var(--color-border, #464646); border-radius: 7px; padding: 0.5rem; }
  .picker-filters { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.4rem; }
  .picker-toggles { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; font-size: 0.75rem; }
  .picker-toggles label { display: flex; align-items: center; gap: 0.3rem; min-height: 44px; }
  .refresh-button { margin-left: auto; }
  .catalog-list { max-height: 320px; overflow-y: auto; display: grid; gap: 0.35rem; }
  .catalog-row { display: flex; border: 1px solid var(--color-border, #464646); border-radius: 8px; background: var(--color-surface, #252525); }
  .catalog-row.selected { border-color: var(--color-primary, #ff1b8d); }
  .model-choice { flex: 1; display: flex; align-items: flex-start; gap: 0.5rem; padding: 0.6rem; min-width: 0; cursor: pointer; }
  .model-choice input { margin-top: 0.2rem; accent-color: var(--color-primary, #ff1b8d); }
  .model-copy { display: grid; gap: 0.2rem; min-width: 0; }
  .model-title { font-size: 0.8rem; overflow-wrap: anywhere; }
  .model-meta, .availability, .attribution { font-size: 0.68rem; color: var(--color-text-muted, #666); }
  .favorite-button { border: 0; background: transparent; font-size: 1.25rem; align-self: flex-start; }
  .favorite-button.favorited { color: var(--color-primary, #ff1b8d); }
  .selected-models { margin: 0; padding: 0; list-style: none; display: grid; gap: 0.35rem; }
  .selected-models li { display: flex; align-items: center; flex-wrap: wrap; gap: 0.35rem; font-size: 0.75rem; }
  .selected-actions { margin-left: auto; display: flex; gap: 0.2rem; }
  .selected-actions button { padding: 0.2rem 0.45rem; }
  .unavailable, .catalog-error { color: #B45309; font-size: 0.7rem; }
  .catalog-error { margin: 0; }
  @media (max-width: 480px) { .picker-filters { grid-template-columns: minmax(0, 1fr); } }
</style>
